import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  BookOpen,
  Upload,
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  ToggleLeft,
  ToggleRight,
  Trash2,
  RefreshCw,
  BarChart3,
  Calendar,
  FileCheck,
  AlertTriangle,
  Building2,
  Globe,
  History,
} from "lucide-react";
import { toast } from "sonner";


// Document type labels
const DOC_TYPE_LABELS = {
  eu_regulation: "EU nařízení",
  czech_regulation: "Český předpis",
  sora_methodology: "SORA metodika",
  reference_document: "Referenční dokument",
  om_template: "OM šablona",
  conops_template: "ConOps šablona",
  sora_rules: "SORA pravidla",
  ucl_requirements: "Požadavky UCL",
};

const REGULATORY_DOC_TYPES = [
  { value: "eu_regulation", label: "EU nařízení (2019/947, 2019/945)" },
  { value: "czech_regulation", label: "Český předpis (L 2 Doplněk X)" },
  { value: "sora_methodology", label: "SORA metodika (EASA)" },
  { value: "reference_document", label: "Referenční dokument (ConOps, OM, provozní dokumenty)" },
];

export const KnowledgeBaseAdmin = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  // State
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState("documents");

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    doc_type: "",
    description: "",
    version: "1.0",
    valid_from: new Date().toISOString().split("T")[0],
    file: null,
    filename: "",
  });

  // Fetch regulatory documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/compliance/knowledge/regulatory`, {
        
      });
      setDocuments(response.data);
    } catch (err) {
      toast.error(t.knowledgeBaseAdmin?.toasts?.fetchError || "Nepodařilo se načíst dokumenty");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get(`/compliance/knowledge/regulatory/stats`, {
        
      });
      setStats(response.data);
    } catch (err) {
      console.error("Stats error:", err);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "platform_admin") {
      fetchDocuments();
      fetchStats();
    }
  }, [user, fetchDocuments, fetchStats]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "docx", "txt", "md"].includes(ext)) {
        toast.error(t.knowledgeBaseAdmin?.toasts?.unsupportedFormat || "Nepodporovaný formát. Použijte .pdf, .docx, .txt nebo .md");
        return;
      }
      
      setUploadForm((prev) => ({
        ...prev,
        file,
        filename: file.name,
      }));
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.doc_type || !uploadForm.description) {
      toast.error(t.knowledgeBaseAdmin?.toasts?.requiredFields || "Vyplňte všechna povinná pole");
      return;
    }

    setUploading(true);
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.readAsDataURL(uploadForm.file);
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        
        // Regulatory documents apply to all SAIL levels by default
        const payload = {
          doc_type: uploadForm.doc_type,
          filename: uploadForm.filename,
          description: uploadForm.description,
          file_content_base64: base64,
          version: uploadForm.version,
          valid_from: uploadForm.valid_from,
          sail_levels_applicable: ["I", "II", "III", "IV", "V", "VI"],
        };

        const response = await api.post(
          `/compliance/knowledge/regulatory/upload`,
          payload
        );

        toast.success((t.knowledgeBaseAdmin?.toasts?.uploaded || "Dokument nahrán: {message}").replace("{message}", response.data.message));
        setShowUploadDialog(false);
        resetUploadForm();
        fetchDocuments();
        fetchStats();
      };
    } catch (err) {
      toast.error(err.response?.data?.detail || t.knowledgeBaseAdmin?.toasts?.uploadError || "Nahrání selhalo");
    } finally {
      setUploading(false);
    }
  };

  // Toggle document active state
  const toggleDocumentActive = async (doc) => {
    try {
      const endpoint = doc.is_active ? "deactivate" : "activate";
      await api.post(`/compliance/knowledge/regulatory/${doc.id}/${endpoint}`, {});
      toast.success(doc.is_active
        ? (t.knowledgeBaseAdmin?.toasts?.deactivated || "Dokument deaktivován")
        : (t.knowledgeBaseAdmin?.toasts?.activated || "Dokument aktivován"));
      fetchDocuments();
      fetchStats();
    } catch (err) {
      toast.error(t.knowledgeBaseAdmin?.toasts?.toggleError || "Operace selhala");
    }
  };

  // Delete document
  const handleDelete = async () => {
    if (!selectedDoc) return;
    
    try {
      await api.delete(`/compliance/knowledge/regulatory/${selectedDoc.id}`);
      toast.success(t.knowledgeBaseAdmin?.toasts?.deleted || "Dokument smazán");
      setShowDeleteDialog(false);
      setSelectedDoc(null);
      fetchDocuments();
      fetchStats();
    } catch (err) {
      toast.error(t.knowledgeBaseAdmin?.toasts?.deleteError || "Smazání selhalo");
    }
  };

  // Reset upload form
  const resetUploadForm = () => {
    setUploadForm({
      doc_type: "",
      description: "",
      version: "1.0",
      valid_from: new Date().toISOString().split("T")[0],
      file: null,
      filename: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Access check
  if (user?.role !== "platform_admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="access-denied">
        <Shield className="h-16 w-16 mb-4" style={{ color: "var(--status-critical)" }} />
        <h2 className="text-xl font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
          Přístup odepřen
        </h2>
        <p style={{ color: "var(--text-muted)" }}>
          Tato stránka je přístupná pouze pro Platform Adminy (Aeroway).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-base-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
            Správa Knowledge Base
          </h1>
          <p style={{ color: "var(--text-muted)" }}>
            Globální regulatorní dokumenty pro všechny organizace
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => {
              fetchDocuments();
              fetchStats();
            }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Obnovit
          </Button>
          <Button
            onClick={() => setShowUploadDialog(true)}
            data-testid="upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Nahrát dokument
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Celkem dokumentů
                </p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                  {stats?.total_documents || 0}
                </p>
              </div>
              <BookOpen className="h-8 w-8" style={{ color: "var(--brand-accent)" }} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-active">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Aktivní dokumenty
                </p>
                <p className="text-2xl font-bold" style={{ color: "var(--status-success)" }}>
                  {stats?.active_documents || 0}
                </p>
              </div>
              <CheckCircle className="h-8 w-8" style={{ color: "var(--status-success)" }} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-eu">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  EU nařízení
                </p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                  {stats?.documents_by_type?.eu_regulation || 0}
                </p>
              </div>
              <Globe className="h-8 w-8" style={{ color: "var(--brand-primary)" }} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-czech">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  České předpisy
                </p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                  {stats?.documents_by_type?.czech_regulation || 0}
                </p>
              </div>
              <Building2 className="h-8 w-8" style={{ color: "var(--brand-accent)" }} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-reference">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Referenční dokumenty
                </p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                  {stats?.documents_by_type?.reference_document || 0}
                </p>
              </div>
              <FileCheck className="h-8 w-8" style={{ color: "var(--status-warning)" }} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="h-4 w-4 mr-2" />
            Regulatorní dokumenty
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">
            <BarChart3 className="h-4 w-4 mr-2" />
            Statistiky použití
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" style={{ color: "var(--brand-accent)" }} />
                Regulatorní dokumenty
              </CardTitle>
              <CardDescription>
                Tyto dokumenty jsou automaticky dostupné všem organizacím při generování ConOps a OM.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin" style={{ color: "var(--brand-accent)" }} />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12" data-testid="no-documents">
                  <FileText className="h-12 w-12 mx-auto mb-4" style={{ color: "var(--text-muted)" }} />
                  <p style={{ color: "var(--text-muted)" }}>
                    Zatím nejsou nahrány žádné regulatorní dokumenty.
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Nahrát první dokument
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dokument</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Verze</TableHead>
                      <TableHead>Platnost od</TableHead>
                      <TableHead>SAIL úrovně</TableHead>
                      <TableHead>Stav</TableHead>
                      <TableHead className="text-right">Akce</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium" style={{ color: "var(--text-primary)" }}>
                              {doc.filename}
                            </p>
                            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                              {doc.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono">v{doc.version}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                            {doc.valid_from ? new Date(doc.valid_from).toLocaleDateString("cs-CZ") : "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {doc.sail_levels_applicable?.map((level) => (
                              <Badge
                                key={level}
                                variant="secondary"
                                className="text-xs"
                              >
                                {level}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            {doc.is_active ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Aktivní
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <XCircle className="h-3 w-3 mr-1" />
                                Neaktivní
                              </Badge>
                            )}
                            {doc.extraction_warning_count > 0 && (
                              <Badge
                                className="bg-amber-100 text-amber-800 border-amber-200"
                                title={`${doc.extraction_warning_count} znaků se nepodařilo extrahovat z dokumentu (pravděpodobně chybějící mapování ligatury ve zdrojovém PDF) — zkontrolujte, jestli text neobsahuje mezery navíc.`}
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Chybí text
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleDocumentActive(doc)}
                              title={doc.is_active ? "Deaktivovat" : "Aktivovat"}
                              data-testid={`toggle-${doc.id}`}
                            >
                              {doc.is_active ? (
                                <ToggleRight className="h-4 w-4" style={{ color: "var(--status-success)" }} />
                              ) : (
                                <ToggleLeft className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedDoc(doc);
                                setShowDeleteDialog(true);
                              }}
                              title="Smazat"
                              data-testid={`delete-${doc.id}`}
                            >
                              <Trash2 className="h-4 w-4" style={{ color: "var(--status-critical)" }} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Dokumenty podle typu
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.documents_by_type && Object.keys(stats.documents_by_type).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(stats.documents_by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span style={{ color: "var(--text-primary)" }}>
                          {DOC_TYPE_LABELS[type] || type}
                        </span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "var(--text-muted)" }}>Žádná data</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Použití v generovaných dokumentech
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.usage_stats && stats.usage_stats.length > 0 ? (
                  <div className="space-y-4">
                    {stats.usage_stats.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="font-mono text-sm" style={{ color: "var(--text-primary)" }}>
                          {item._id}
                        </span>
                        <Badge>{item.usage_count}x použito</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "var(--text-muted)" }}>
                    Zatím nebyly generovány žádné dokumenty.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Nahrát regulatorní dokument</DialogTitle>
            <DialogDescription>
              Dokument bude dostupný všem organizacím při generování compliance dokumentů.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Document Type */}
            <div className="space-y-2">
              <Label htmlFor="doc_type">Typ dokumentu *</Label>
              <Select
                value={uploadForm.doc_type}
                onValueChange={(value) =>
                  setUploadForm((prev) => ({ ...prev, doc_type: value }))
                }
              >
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue placeholder="Vyberte typ dokumentu" />
                </SelectTrigger>
                <SelectContent>
                  {REGULATORY_DOC_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Popis *</Label>
              <Textarea
                id="description"
                placeholder="Např. Prováděcí nařízení Komise (EU) 2019/947 - pravidla pro UAS"
                value={uploadForm.description}
                onChange={(e) =>
                  setUploadForm((prev) => ({ ...prev, description: e.target.value }))
                }
                data-testid="description-input"
              />
            </div>

            {/* Version and Valid From */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">Verze</Label>
                <Input
                  id="version"
                  placeholder="1.0"
                  value={uploadForm.version}
                  onChange={(e) =>
                    setUploadForm((prev) => ({ ...prev, version: e.target.value }))
                  }
                  data-testid="version-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_from">Platnost od</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={uploadForm.valid_from}
                  onChange={(e) =>
                    setUploadForm((prev) => ({ ...prev, valid_from: e.target.value }))
                  }
                  data-testid="valid-from-input"
                />
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="file">Soubor *</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="flex-1"
                  data-testid="file-input"
                />
              </div>
              {uploadForm.filename && (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Vybraný soubor: {uploadForm.filename}
                </p>
              )}
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                Podporované formáty: .pdf, .docx, .txt, .md
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                resetUploadForm();
              }}
            >
              {t.common?.cancel || 'Zrušit'}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file || !uploadForm.doc_type}
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t.common?.uploading || 'Nahrávám...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t.common?.upload || 'Nahrát'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" style={{ color: "var(--status-warning)" }} />
              Smazat dokument
            </DialogTitle>
            <DialogDescription>
              Opravdu chcete smazat dokument "{selectedDoc?.filename}"? Tato akce je nevratná.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedDoc(null);
              }}
            >
              Zrušit
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Smazat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBaseAdmin;
